import { useState, useMemo } from "react";
import { Printer, ChevronLeft, ChevronRight, FileDown, Minus, Plus } from "lucide-react";
import { fmtHours, fmtMoney } from "@/lib/calc";

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const toStr = (d) => d.toISOString().slice(0, 10);
const ukDate = (d, opts) => d.toLocaleDateString("en-GB", opts);
const weekRangeLabel = (start) => `${ukDate(start, { day: "2-digit", month: "short" })} – ${ukDate(addDays(start, 6), { day: "2-digit", month: "short", year: "2-digit" })}`;

export default function PrintWeek({ entries, currency, userName }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [weeks, setWeeks] = useState(2);

  const rangeStart = weekStart;
  const rangeEnd = useMemo(() => addDays(weekStart, weeks * 7 - 1), [weekStart, weeks]);
  const startStr = toStr(rangeStart);
  const endStr = toStr(rangeEnd);

  // Group entries by week within the chosen range
  const weekGroups = useMemo(() => {
    return Array.from({ length: weeks }, (_, w) => {
      const wStart = addDays(weekStart, w * 7);
      const wStartStr = toStr(wStart);
      const wEndStr = toStr(addDays(wStart, 6));
      const items = entries
        .filter((e) => e.date >= wStartStr && e.date <= wEndStr)
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      const subtotal = items.reduce(
        (acc, e) => ({ hours: acc.hours + e.duration_hours, earnings: acc.earnings + e.earnings }),
        { hours: 0, earnings: 0 }
      );
      return { start: wStart, label: weekRangeLabel(wStart), items, subtotal };
    });
  }, [entries, weekStart, weeks]);

  const grand = weekGroups.reduce(
    (acc, g) => ({ hours: acc.hours + g.subtotal.hours, earnings: acc.earnings + g.subtotal.earnings, count: acc.count + g.items.length }),
    { hours: 0, earnings: 0, count: 0 }
  );

  const rangeLabel = `${ukDate(rangeStart, { day: "2-digit", month: "short", year: "numeric" })} – ${ukDate(rangeEnd, { day: "2-digit", month: "short", year: "numeric" })}`;

  const handlePrint = () => {
    const sections = weekGroups
      .map((g) => {
        const rows = g.items
          .map(
            (e) => `<tr>
              <td>${ukDate(new Date(e.date + "T00:00:00"), { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" })}</td>
              <td>${e.project}</td>
              <td>${e.start_time}–${e.end_time}</td>
              <td class="num">${Number(e.multiplier).toFixed(2)}x</td>
              <td class="num">${e.duration_hours.toFixed(2)}</td>
              <td class="num">${currency}${Number(e.earnings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td>${(e.notes || "").replace(/</g, "&lt;")}</td>
            </tr>`
          )
          .join("");
        const body = g.items.length
          ? `<tbody>${rows}</tbody>
             <tfoot><tr>
               <td colspan="4">Week Subtotal</td>
               <td class="num">${g.subtotal.hours.toFixed(2)}</td>
               <td class="num">${currency}${g.subtotal.earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
               <td></td>
             </tr></tfoot>`
          : `<tbody><tr><td colspan="7" class="empty">No overtime logged this week.</td></tr></tbody>`;
        return `<div class="week">
          <h2>Week of ${g.label}</h2>
          <table>
            <thead><tr>
              <th>Date</th><th>Project</th><th>Time</th><th class="num">Rate</th><th class="num">Hours</th><th class="num">Payout</th><th>Notes</th>
            </tr></thead>
            ${body}
          </table>
        </div>`;
      })
      .join("");

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Overtime ${startStr} to ${endStr}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #111; margin: 40px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #111; padding-bottom: 12px; margin-bottom: 8px; }
  .brand { font-size: 12px; letter-spacing: 3px; text-transform: uppercase; color: #b45309; font-weight: 700; }
  h1 { font-size: 26px; margin: 4px 0 0; text-transform: uppercase; letter-spacing: -0.5px; }
  .meta { text-align: right; font-size: 13px; color: #444; }
  .meta strong { color: #111; }
  .week { margin-top: 26px; page-break-inside: avoid; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #b45309; margin: 0 0 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; text-transform: uppercase; font-size: 10px; letter-spacing: 1px; color: #666; border-bottom: 2px solid #ccc; padding: 7px 6px; }
  td { padding: 8px 6px; border-bottom: 1px solid #eee; }
  td.num, th.num { text-align: right; }
  td.empty { color: #999; font-style: italic; }
  tfoot td { border-top: 1.5px solid #111; border-bottom: none; font-weight: 700; font-size: 13px; }
  .grand { margin-top: 30px; border-top: 3px solid #111; padding-top: 12px; display: flex; justify-content: flex-end; gap: 28px; font-size: 16px; font-weight: 700; }
  .foot { margin-top: 30px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }
  @media print { body { margin: 20px; } }
</style></head>
<body>
  <div class="head">
    <div>
      <div class="brand">TimeTrak</div>
      <h1>Overtime Report</h1>
    </div>
    <div class="meta">
      ${userName ? `<div><strong>${userName}</strong></div>` : ""}
      <div>Period: <strong>${rangeLabel}</strong></div>
      <div>${weeks} ${weeks === 1 ? "week" : "weeks"} · Generated ${ukDate(new Date(), { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>
  </div>
  ${sections}
  <div class="grand">
    <span>Total: ${grand.hours.toFixed(2)} hrs</span>
    <span>${currency}${grand.earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
  </div>
  <div class="foot">TimeTrak — ${grand.count} ${grand.count === 1 ? "entry" : "entries"} across ${weeks} ${weeks === 1 ? "week" : "weeks"} (${startStr} to ${endStr}).</div>
  <script>window.onload = function(){ window.print(); }</script>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="print-week" className="fade-up max-w-3xl">
      <div className="bg-[#181a22] border border-[#2b2f3d] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <Printer className="w-5 h-5 text-amber-500" />
          <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#f3f4f8]">Select Weeks</h3>
        </div>

        {/* Start week picker */}
        <label className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Start week</label>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <button data-testid="print-prev-week" onClick={() => setWeekStart((w) => addDays(w, -7))} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid="print-week-label" className="flex-1 text-center font-mono-data text-sm text-[#f3f4f8] px-3 py-2.5 rounded-lg bg-[#14161c] border border-[#2b2f3d]">{weekRangeLabel(weekStart)}</span>
          <button data-testid="print-next-week" onClick={() => setWeekStart((w) => addDays(w, 7))} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Number of weeks */}
        <div className="mt-4 flex items-center justify-between">
          <label className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Number of weeks</label>
          <div className="flex items-center gap-3">
            <button data-testid="print-weeks-minus" onClick={() => setWeeks((n) => Math.max(1, n - 1))} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors disabled:opacity-40" disabled={weeks <= 1}>
              <Minus className="w-4 h-4" />
            </button>
            <span data-testid="print-weeks-count" className="font-mono-data font-bold text-lg text-amber-500 w-8 text-center">{weeks}</span>
            <button data-testid="print-weeks-plus" onClick={() => setWeeks((n) => Math.min(12, n + 1))} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors disabled:opacity-40" disabled={weeks >= 12}>
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="mt-2 font-mono-data text-[11px] text-[#62697e]">Covering {rangeLabel}</p>

        {/* Preview grouped by week */}
        <div className="mt-4 bg-[#14161c] border border-[#2b2f3d] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">
              {grand.count} {grand.count === 1 ? "entry" : "entries"} · {weeks} {weeks === 1 ? "week" : "weeks"}
            </span>
            <div className="flex items-center gap-4">
              <span className="font-mono-data font-bold text-amber-500">{fmtHours(grand.hours)}</span>
              <span className="font-mono-data font-bold text-emerald-500">{fmtMoney(grand.earnings, currency)}</span>
            </div>
          </div>
          <div className="max-h-64 overflow-auto space-y-3">
            {weekGroups.map((g, i) => (
              <div key={i}>
                <div className="flex items-center justify-between font-mono-data text-[11px] uppercase tracking-wide text-amber-500/90 mb-1.5">
                  <span>Week of {g.label}</span>
                  <span className="text-[#9fa6b9]">{g.subtotal.hours.toFixed(2)}h · {fmtMoney(g.subtotal.earnings, currency)}</span>
                </div>
                {g.items.length === 0 ? (
                  <p className="font-mono-data text-xs text-[#62697e] pl-1">— nothing logged —</p>
                ) : (
                  g.items.map((e) => (
                    <div key={e.id} className="flex items-center justify-between font-mono-data text-xs text-[#9fa6b9] py-1 border-b border-[#2b2f3d]/40 last:border-0">
                      <span>{ukDate(new Date(e.date + "T00:00:00"), { weekday: "short", day: "2-digit", month: "2-digit" })} · {e.project} · {e.start_time}–{e.end_time}</span>
                      <span className="text-[#f3f4f8]">{e.duration_hours.toFixed(2)}h · {fmtMoney(e.earnings, currency)}</span>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          data-testid="print-generate-button"
          onClick={handlePrint}
          disabled={grand.count === 0}
          className="mt-4 w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-[#0c0d10] font-display font-bold uppercase tracking-wide rounded-lg px-4 py-3 transition-colors active:scale-[0.99]"
        >
          <FileDown className="w-4 h-4" /> Generate PDF
        </button>
        <p className="mt-2 text-center font-mono-data text-[11px] text-[#62697e]">Opens a print view — choose "Save as PDF" as the destination.</p>
      </div>
    </div>
  );
}
